import { createFileRoute, redirect, notFound, Link } from '@tanstack/react-router';
import { Button, Card, Flex, Space, Typography, theme } from 'antd';
import { HomeOutlined, LogoutOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { SidebarLayout } from '@/components/layout/SidebarLayout';
import { navigationConfig } from '@/utils/navigation';
import { useNavigationItems } from '@/hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLogoutMutation } from '@/api/auth/mutations';
import { isAdminEquivalentRole, normalizeUserRole } from '@/utils/authz';
import { USER_ROLES } from '@/constants/roles';
import { AppointmentProvider } from '@/contexts/AppointmentContext';

/**
 * Route configuration and access control for /manage.
 *
 * Layer 1 (here): `beforeLoad` checks authentication and global role via
 * `context.auth` — the decoded JWT injected by Router.tsx from the shared
 * AuthProvider state (not a per-route hook instance).
 *
 * Allowed global roles: USER, ADMIN, DEVELOPER (admin-equivalent via {@link isAdminEquivalentRole}).
 */
export const Route = createFileRoute('/manage')({
  component: ManageLayout,
  beforeLoad: ({ context, location }) => {
    // Redirect unauthenticated users to login page
    if (!context?.auth) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href } as any,
      });
    }

    const role = normalizeUserRole(context.auth?.role);
    if (role !== USER_ROLES.USER && !isAdminEquivalentRole(role)) {
      throw notFound();
    }
  },
});

/**
 * Full-page view when a non-admin user has no tenant context (no organization access).
 * Shows a clear message and Log out so they are not stuck in the app with empty/confusing UI.
 */
function NoOrganizationAccessView() {
  const { t } = useTranslation(['common']);
  const { token } = theme.useToken();
  const logout = useLogoutMutation();

  return (
    <Flex
      align="center"
      justify="center"
      style={{
        minHeight: '100vh',
        background: token.colorBgLayout,
        padding: 24
      }}
    >
      <Card
        style={{
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary
        }}
        styles={{ body: { padding: '40px 32px' } }}
      >
        <Flex vertical align="center" gap={24}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: token.colorPrimaryBg,
              color: token.colorPrimary
            }}
          >
            <TeamOutlined style={{ fontSize: 32 }} />
          </Flex>

          <Flex vertical gap={8}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t('common:ui.tenantSelector.noOrganizationsTitle')}
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {t('common:ui.tenantSelector.noOrganizationsDescription')}
            </Typography.Paragraph>
          </Flex>

          <Space wrap size="middle" style={{ justifyContent: 'center' }}>
            <Link to="/">
              <Button type="primary" icon={<HomeOutlined />}>
                {t('common:userMenu.home')}
              </Button>
            </Link>
            <Link to="/profile">
              <Button icon={<UserOutlined />}>{t('common:userMenu.profile')}</Button>
            </Link>
            <Button
              icon={<LogoutOutlined />}
              onClick={() => logout.mutate()}
              loading={logout.isPending}
              danger
            >
              {logout.isPending ? t('common:userMenu.signingOut') : t('common:userMenu.signOut')}
            </Button>
          </Space>
        </Flex>
      </Card>
    </Flex>
  );
}

/**
 * Management area layout with sidebar navigation.
 *
 * Access is checked in two layers:
 * 1. `beforeLoad` (above) — global role gate via router `context.auth`
 * 2. Runtime (here) — non-admin users without `clientId`/`companyId` in JWT
 *    see {@link NoOrganizationAccessView} instead of an empty sidebar
 *
 * Uses shared auth from {@link useAuth} (AuthProvider context). The loading
 * guard prevents a flash of NoOrganizationAccessView for admins — admins
 * often have no tenant in JWT and rely on `adminClientId` URL scope instead.
 */
function ManageLayout() {
  const config = navigationConfig.manage;
  const navigationItems = useNavigationItems('manage');
  const { t } = useTranslation('navigation');
  const { payload, loading } = useAuth();

  // Wait for shared auth; avoids false no-org view on first paint
  if (loading) {
    return null;
  }

  const isAdmin = isAdminEquivalentRole(payload?.role);
  const hasTenantContext = !!(payload?.clientId || payload?.companyId);

  if (!isAdmin && !hasTenantContext) {
    return <NoOrganizationAccessView />;
  }

  return (
    <AppointmentProvider>
      <SidebarLayout
        context="manage"
        title={t(config.titleKey)}
        navigationItems={navigationItems}
        expandedSiderWidth={config.expandedWidth}
        collapsedSiderWidth={config.collapsedWidth}
      />
    </AppointmentProvider>
  );
}
